<template>
  <AppSidebar>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Showcase Registry</h2>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Manage portfolio page content and project assets</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="border-b border-gray-100 dark:border-gray-700">
          <nav class="flex overflow-x-auto custom-scrollbar">
            <button v-for="tab in tabs" :key="tab.id" 
              @click="activeTab = tab.id"
              class="px-8 py-5 text-[10px] font-black uppercase tracking-widest transition-all duration-300 group whitespace-nowrap border-b-2"
              :class="activeTab === tab.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'">
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <div class="p-6 sm:p-8">
          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>

          <div v-else>
            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero'" class="space-y-6">
              <div class="max-w-3xl">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6" style="font-family: Poppins, sans-serif">Hero Configuration</h3>
                <form @submit.prevent="saveSection('hero')" class="space-y-5">
                  <div>
                    <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Headline</label>
                    <input 
                      v-model="sections.hero.data.title" 
                      type="text" 
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Sub-Title</label>
                    <textarea 
                      v-model="sections.hero.data.subtitle" 
                      rows="3"
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"
                    ></textarea>
                  </div>
                  <div class="flex justify-end">
                    <button type="submit" class="px-6 py-2.5 bg-primary-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20">Save Changes</button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Intro Section Tab -->
            <div v-if="activeTab === 'intro'" class="space-y-6">
              <div class="max-w-3xl">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6" style="font-family: Poppins, sans-serif">Intro Configuration</h3>
                <form @submit.prevent="saveSection('intro')" class="space-y-5">
                  <div>
                    <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Headline</label>
                    <input 
                      v-model="sections.intro.data.title" 
                      type="text" 
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Description</label>
                    <textarea 
                      v-model="sections.intro.data.description" 
                      rows="4"
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"
                    ></textarea>
                  </div>
                  <div class="flex justify-end">
                    <button type="submit" class="px-6 py-2.5 bg-primary-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20">Save Changes</button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Portfolio Items Tab -->
            <div v-if="activeTab === 'portfolio'" class="space-y-6">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest" style="font-family: Poppins, sans-serif">Project Assets</h3>
                <button @click="openPortfolioModal()" class="btn btn-primary flex items-center justify-center gap-2 px-6 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-bold">
                  <PlusIcon class="w-4 h-4" />
                  Add Project
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div 
                  v-for="item in portfolioItems" 
                  :key="item.id"
                  class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden hover:shadow-xl transition-all group relative"
                >
                  <div class="h-48 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
                    <img 
                      v-if="item.featured_image_url" 
                      :src="item.featured_image_url" 
                      class="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center text-gray-200 dark:text-gray-800">
                      <PhotoIcon class="w-12 h-12" />
                    </div>
                    <div class="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        @click="openPortfolioModal(item)"
                        class="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-lg text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors backdrop-blur-sm"
                      >
                        <PencilIcon class="w-4 h-4" />
                      </button>
                      <button 
                        @click="deletePortfolio(item.id)"
                        class="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-lg text-gray-600 dark:text-gray-400 hover:text-red-600 transition-colors backdrop-blur-sm"
                      >
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <div class="absolute top-4 left-4">
                      <span class="px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full border shadow-sm backdrop-blur-sm" :class="{
                        'bg-green-500/10 text-green-600 border-green-500/20': item.status === 'active',
                        'bg-gray-500/10 text-gray-400 border-gray-500/20': item.status === 'inactive',
                        'bg-yellow-500/10 text-yellow-600 border-yellow-500/20': item.status === 'draft'
                      }">
                        {{ item.status }}
                      </span>
                    </div>
                  </div>
                  <div class="p-6">
                    <div class="flex justify-between items-start mb-4">
                      <span class="text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded border border-primary-100 dark:border-primary-800">
                        {{ item.category }}
                      </span>
                      <span v-if="item.featured" class="text-yellow-500" title="Featured">
                        <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/></svg>
                      </span>
                    </div>
                    <h4 class="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-2 line-clamp-1 italic group-hover:text-primary-600 transition-colors" style="font-family: Poppins, sans-serif">{{ item.title }}</h4>
                    <p class="text-gray-500 dark:text-gray-400 text-xs line-clamp-2 mb-6 leading-relaxed h-8">{{ item.description }}</p>
                    <div class="flex items-center justify-between pt-5 border-t border-gray-50 dark:border-gray-700/50">
                      <span class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Client: {{ item.client_name }}</span>
                      <button @click="openPortfolioModal(item)" class="text-[9px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest hover:underline italic">View Details</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Portfolio Modal -->
      <div v-if="showPortfolioModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity" @click="closePortfolioModal"></div>

          <div class="relative bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-5xl sm:w-full max-h-[90vh] flex flex-col border border-gray-100 dark:border-gray-700">
            <div class="p-6 sm:p-8 overflow-y-auto flex-grow custom-scrollbar">
              <div class="w-full">
                <h3 class="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-8" id="modal-title" style="font-family: Poppins, sans-serif">
                  {{ editingItem ? 'Edit Project' : 'Add New Project' }}
                </h3>
                
                <!-- Form Tabs -->
                <div class="border-b border-gray-100 dark:border-gray-700 mb-8">
                  <nav class="flex space-x-8">
                    <button
                      v-for="formTab in formTabs"
                      :key="formTab.id"
                      @click="activeFormTab = formTab.id"
                      type="button"
                      class="pb-4 px-1 border-b-2 text-[10px] font-black uppercase tracking-widest transition-all"
                      :class="[
                        activeFormTab === formTab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                      ]"
                    >
                      {{ formTab.name }}
                    </button>
                  </nav>
                </div>

                <div class="space-y-6">
                  <!-- Basic Info Tab -->
                   <div v-show="activeFormTab === 'basic'" class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Project Name *</label>
                        <input v-model="portfolioForm.title" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Client Name *</label>
                        <input v-model="portfolioForm.client_name" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                    </div>

                     <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Category *</label>
                        <input v-model="portfolioForm.category" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., Web Development" />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Status *</label>
                        <select v-model="portfolioForm.status" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm appearance-none">
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div class="flex items-center pt-5">
                        <label class="flex items-center cursor-pointer group">
                          <input v-model="portfolioForm.featured" type="checkbox" class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent" />
                          <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest group-hover:text-primary-600 transition-colors">Featured Status</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Short Summary *</label>
                      <textarea v-model="portfolioForm.description" rows="2" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none" required></textarea>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Full Description</label>
                      <textarea v-model="portfolioForm.long_description" rows="5" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"></textarea>
                    </div>

                     <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Project Link</label>
                        <input v-model="portfolioForm.project_url" type="url" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="https://example.com" />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">GitHub Link</label>
                        <input v-model="portfolioForm.github_url" type="url" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="https://github.com/..." />
                      </div>
                    </div>

                     <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Timeline (Weeks)</label>
                        <input v-model.number="portfolioForm.timeline_weeks" type="number" min="1" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="Weeks" />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Team Size</label>
                        <input v-model.number="portfolioForm.team_size" type="number" min="1" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="Members" />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Completion Date</label>
                        <input v-model="portfolioForm.completed_at" type="date" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" />
                      </div>
                    </div>
                  </div>

                  <!-- Technologies & Features Tab -->
                  <div v-show="activeFormTab === 'details'" class="space-y-8">
                    <!-- Technologies -->
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Technologies Used</label>
                      <div class="space-y-3">
                        <div v-for="(tech, index) in portfolioForm.technologies" :key="index" class="flex gap-3">
                          <input v-model="portfolioForm.technologies[index]" type="text" class="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., Vue.js" />
                          <button @click="removeTechnology(index)" type="button" class="p-2.5 text-gray-400 hover:text-red-500 transition-colors">
                            <TrashIcon class="w-5 h-5" />
                          </button>
                        </div>
                        <button @click="addTechnology" type="button" class="w-full py-3 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-xl text-gray-400 dark:text-gray-500 hover:border-primary-500 hover:text-primary-600 transition-all text-[10px] font-bold uppercase tracking-widest">
                          + Add Technology
                        </button>
                      </div>
                    </div>

                    <!-- Features -->
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Key Features</label>
                      <div class="space-y-3">
                        <div v-for="(feature, index) in portfolioForm.features" :key="index" class="flex gap-3">
                          <input v-model="portfolioForm.features[index]" type="text" class="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., Real-time notifications" />
                          <button @click="removeFeature(index)" type="button" class="p-2.5 text-gray-400 hover:text-red-500 transition-colors">
                            <TrashIcon class="w-5 h-5" />
                          </button>
                        </div>
                        <button @click="addFeature" type="button" class="w-full py-3 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-xl text-gray-400 dark:text-gray-500 hover:border-primary-500 hover:text-primary-600 transition-all text-[10px] font-bold uppercase tracking-widest">
                          + Add Feature
                        </button>
                      </div>
                    </div>

                    <!-- Results -->
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Project Results</label>
                      <div class="space-y-3">
                        <div v-for="(result, index) in portfolioForm.results" :key="index" class="flex gap-3">
                          <input v-model="portfolioForm.results[index]" type="text" class="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., 50% increase in user engagement" />
                          <button @click="removeResult(index)" type="button" class="p-2.5 text-gray-400 hover:text-red-500 transition-colors">
                            <TrashIcon class="w-5 h-5" />
                          </button>
                        </div>
                        <button @click="addResult" type="button" class="w-full py-3 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-xl text-gray-400 dark:text-gray-500 hover:border-primary-500 hover:text-primary-600 transition-all text-[10px] font-bold uppercase tracking-widest">
                          + Add Result
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Images Tab -->
                  <div v-show="activeFormTab === 'images'" class="space-y-8">
                    <!-- Featured Image -->
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Featured Image</label>
                      <div class="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700">
                        <ImageUpload
                          v-model="featuredImageFile"
                          :model-value="editingItem?.featured_image_url"
                          @change="handleFeaturedImageChange"
                          placeholder="Upload Featured Image"
                        />
                      </div>
                    </div>

                    <!-- Screenshots -->
                    <div v-if="editingItem">
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Project Screenshots</label>
                      <div class="grid grid-cols-2 md:grid-cols-3 gap-5 mb-6">
                        <div v-for="screenshot in currentScreenshots" :key="screenshot.id" class="relative group">
                          <img :src="screenshot.image_url" :alt="screenshot.caption" class="w-full h-32 object-cover rounded-xl border border-gray-100 dark:border-gray-700" />
                          <div class="absolute inset-0 bg-gray-900/60 opacity-0 group-hover:opacity-100 transition-all rounded-xl flex items-center justify-center backdrop-blur-sm">
                            <button @click="deleteScreenshotItem(screenshot.id)" type="button" class="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg transform scale-90 group-hover:scale-100 transition-all">
                              <TrashIcon class="w-5 h-5" />
                            </button>
                          </div>
                          <input v-model="screenshot.caption" type="text" placeholder="Caption..." class="mt-2 w-full px-2 py-1 text-[10px] bg-transparent border-b border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 focus:outline-none focus:border-primary-500 transition-all font-bold uppercase tracking-widest" @blur="updateScreenshotCaption(screenshot)" />
                        </div>
                      </div>
                      <div class="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700">
                        <ImageUpload
                          v-model="newScreenshotFile"
                          @change="handleScreenshotUpload"
                          placeholder="Upload Screenshot"
                          hint="Target: 16:9 • WebP Optimized"
                        />
                      </div>
                    </div>
                    <div v-else class="p-12 text-center bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                      <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest italic">Save Project to Enable Screenshots</p>
                    </div>
                  </div>

                  <!-- Services Tab -->
                  <div v-show="activeFormTab === 'services'" class="space-y-6">
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Related Services</label>
                      <p class="text-[10px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-widest mb-6 italic">Select services related to this project</p>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto custom-scrollbar p-1">
                        <label v-for="service in availableServices" :key="service.id" class="flex items-center p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 rounded-2xl hover:bg-white dark:hover:bg-gray-800 hover:border-primary-500/50 cursor-pointer transition-all group">
                          <input
                            type="checkbox"
                            :value="service.id"
                            v-model="selectedServiceIds"
                            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent"
                          />
                          <div class="ml-4 flex-1">
                            <p class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider group-hover:text-primary-600 transition-colors">{{ service.title }}</p>
                            <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{{ service.category?.name || 'Standard' }}</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="bg-gray-50 dark:bg-gray-900 px-6 py-5 sm:px-8 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-100 dark:border-gray-700">
              <button 
                type="button" 
                @click="savePortfolio"
                :disabled="saving"
                class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg shadow-primary-500/20 px-6 py-2.5 bg-primary-600 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-primary-700 sm:w-auto transition-all disabled:opacity-50"
              >
                <span v-if="saving">Processing...</span>
                <span v-else>{{ editingItem ? 'Save Project' : 'Create Project' }}</span>
              </button>
              <button 
                type="button" 
                @click="closePortfolioModal"
                class="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm px-6 py-2.5 bg-white dark:bg-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useContentStore } from '@/stores/content'
import { usePortfolioStore } from '@/stores/portfolio'
import { useServiceStore } from '@/stores/services'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import ImageUpload from '@/components/ImageUpload.vue'

const contentStore = useContentStore()
const portfolioStore = usePortfolioStore()
const serviceStore = useServiceStore()

const loading = ref(true)
const saving = ref(false)
const activeTab = ref('hero')
const activeFormTab = ref('basic')
const showPortfolioModal = ref(false)
const editingItem = ref(null)

// Image upload states
const featuredImageFile = ref(null)
const newScreenshotFile = ref(null)
const currentScreenshots = ref([])

// Service selection
const selectedServiceIds = ref([])
const availableServices = ref([])

// Form state
const portfolioForm = ref({
  title: '',
  category: '',
  client_name: '',
  description: '',
  long_description: '',
  technologies: [],
  features: [],
  results: [],
  project_url: '',
  github_url: '',
  timeline_weeks: null,
  team_size: null,
  completed_at: null,
  featured: false,
  status: 'draft'
})

const tabs = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'intro', name: 'Intro Section' },
  { id: 'portfolio', name: 'Portfolio Projects' }
]

const formTabs = [
  { id: 'basic', name: 'Basic Info' },
  { id: 'details', name: 'Technologies & Features' },
  { id: 'images', name: 'Images' },
  { id: 'services', name: 'Related Services' }
]

// Data loading
onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('portfolio'),
      portfolioStore.fetchPortfolio(),
      serviceStore.fetchServices()
    ])
    
    availableServices.value = serviceStore.services
    
    // Initialize sections if they don't exist
    if (!sections.value.hero) {
      sections.value.hero = { section: 'hero', data: { title: '', subtitle: '' } }
    }
    if (!sections.value.intro) {
      sections.value.intro = { section: 'intro', data: { title: '', description: '' } }
    }
  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})

// Computed
const rawSections = computed(() => contentStore.pageSections.portfolio || [])
const sections = computed(() => {
  const map = {}
  rawSections.value.forEach(s => map[s.section] = JSON.parse(JSON.stringify(s)))
  
  // Defaults
  if (!map.hero) map.hero = { section: 'hero', data: { title: '', subtitle: '' } }
  if (!map.intro) map.intro = { section: 'intro', data: { title: '', description: '' } }
  
  return map
})

const portfolioItems = computed(() => portfolioStore.portfolioItems)

// Methods
const saveSection = async (sectionName) => {
  const section = sections.value[sectionName]
  try {
    if (section.id) {
      await contentStore.updatePageSection(section.id, {
        page: 'portfolio',
        section: sectionName,
        data: section.data
      })
    } else {
      await contentStore.createPageSection({
        page: 'portfolio',
        section: sectionName,
        data: section.data
      })
    }
    // Refresh
    await contentStore.fetchPageSections('portfolio')
  } catch (error) {
    console.error('Error saving section:', error)
  }
}

// Array management methods
const addTechnology = () => {
  portfolioForm.value.technologies.push('')
}

const removeTechnology = (index) => {
  portfolioForm.value.technologies.splice(index, 1)
}

const addFeature = () => {
  portfolioForm.value.features.push('')
}

const removeFeature = (index) => {
  portfolioForm.value.features.splice(index, 1)
}

const addResult = () => {
  portfolioForm.value.results.push('')
}

const removeResult = (index) => {
  portfolioForm.value.results.splice(index, 1)
}

// Portfolio CRUD
const openPortfolioModal = async (item = null) => {
  editingItem.value = item
  activeFormTab.value = 'basic'
  featuredImageFile.value = null
  newScreenshotFile.value = null
  currentScreenshots.value = []
  
  if (item) {
    portfolioForm.value = {
      title: item.title || '',
      category: item.category || '',
      client_name: item.client_name || '',
      description: item.description || '',
      long_description: item.long_description || '',
      technologies: item.technologies || [],
      features: item.features || [],
      results: item.results || [],
      project_url: item.project_url || '',
      github_url: item.github_url || '',
      timeline_weeks: item.timeline_weeks || null,
      team_size: item.team_size || null,
      completed_at: item.completed_at || null,
      featured: item.featured || false,
      status: item.status || 'draft'
    }
    
    // Load screenshots if editing
    if (item.screenshots) {
      currentScreenshots.value = [...item.screenshots]
    }
    
    // Load selected services
    if (item.services) {
      selectedServiceIds.value = item.services.map(s => s.id)
    } else {
      selectedServiceIds.value = []
    }
  } else {
    portfolioForm.value = {
      title: '',
      category: '',
      client_name: '',
      description: '',
      long_description: '',
      technologies: [],
      features: [],
      results: [],
      project_url: '',
      github_url: '',
      timeline_weeks: null,
      team_size: null,
      completed_at: null,
      featured: false,
      status: 'draft'
    }
    selectedServiceIds.value = []
  }
  showPortfolioModal.value = true
}

const closePortfolioModal = () => {
  showPortfolioModal.value = false
  editingItem.value = null
  featuredImageFile.value = null
  newScreenshotFile.value = null
}

const handleFeaturedImageChange = (file) => {
  featuredImageFile.value = file
}

const handleScreenshotUpload = async (file) => {
  if (!file || !editingItem.value) return
  
  try {
    const screenshot = await portfolioStore.uploadScreenshot(editingItem.value.id, file)
    currentScreenshots.value.push(screenshot)
    newScreenshotFile.value = null
  } catch (error) {
    console.error('Error uploading screenshot:', error)
  }
}

const deleteScreenshotItem = async (screenshotId) => {
  if (!confirm('Are you sure you want to delete this screenshot?')) return
  
  try {
    await portfolioStore.deleteScreenshot(screenshotId)
    currentScreenshots.value = currentScreenshots.value.filter(s => s.id !== screenshotId)
  } catch (error) {
    console.error('Error deleting screenshot:', error)
  }
}

const updateScreenshotCaption = async (screenshot) => {
  // This would need a backend endpoint to update caption
  // For now, we'll just update locally
  console.log('Update caption:', screenshot)
}

const savePortfolio = async () => {
  saving.value = true
  try {
    // Clean up arrays - remove empty strings
    const formData = {
      ...portfolioForm.value,
      technologies: portfolioForm.value.technologies.filter(t => t.trim() !== ''),
      features: portfolioForm.value.features.filter(f => f.trim() !== ''),
      results: portfolioForm.value.results.filter(r => r.trim() !== '')
    }
    
    let portfolioId
    
    if (editingItem.value) {
      await portfolioStore.updatePortfolio(editingItem.value.id, formData)
      portfolioId = editingItem.value.id
    } else {
      const newItem = await portfolioStore.addPortfolio(formData)
      portfolioId = newItem.id
      editingItem.value = newItem
    }
    
    // Upload featured image if changed
    if (featuredImageFile.value) {
      await portfolioStore.uploadImage(portfolioId, featuredImageFile.value)
      // Immediately refresh to get the updated image URL
      await portfolioStore.fetchPortfolio()
    }
    
    // Attach services
    if (selectedServiceIds.value.length > 0) {
      await portfolioStore.attachServices(portfolioId, selectedServiceIds.value)
    }
    
    // Refresh portfolio list
    await portfolioStore.fetchPortfolio()
    
    closePortfolioModal()
  } catch (error) {
    console.error('Error saving portfolio item:', error)
  } finally {
    saving.value = false
  }
}

const deletePortfolio = async (id) => {
  if (confirm('Are you sure you want to delete this project?')) {
    await portfolioStore.deletePortfolio(id)
  }
}
</script>
